'use client';

import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, Gem, Sparkles } from 'lucide-react';

interface PaymentSuccessModalProps {
  isOpen: boolean;
  oldBalance: number;
  newBalance: number;
  creditsAdded: number;
  onClose: () => void;
  title?: string;
}

function AnimatedCounter({
  from,
  to,
  duration = 1500,
}: {
  from: number;
  to: number;
  duration?: number;
}) {
  const [count, setCount] = useState(from);

  useEffect(() => {
    if (from === to) {
      setCount(to);
      return;
    }

    const startTime = Date.now();
    const diff = to - from;

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easeOutExpo = 1 - Math.pow(2, -10 * progress);

      setCount(Math.round(from + diff * easeOutExpo));

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }, [from, to, duration]);

  return <span>{count}</span>;
}

function Particle({ delay }: { delay: number }) {
  const randomX = Math.random() * 200 - 100;
  const randomRotate = Math.random() * 360;

  return (
    <motion.div
      className="absolute h-2 w-2 rounded-full"
      style={{
        background: `hsl(${Math.random() * 60 + 180}, 80%, 60%)`,
        left: '50%',
        top: '50%',
      }}
      initial={{ opacity: 0, scale: 0, x: 0, y: 0 }}
      animate={{
        opacity: [0, 1, 1, 0],
        scale: [0, 1, 1, 0.5],
        x: randomX,
        y: [0, -100 - Math.random() * 50],
        rotate: randomRotate,
      }}
      transition={{
        duration: 1.5,
        delay,
        ease: 'easeOut',
      }}
    />
  );
}

export function PaymentSuccessModal({
  isOpen,
  oldBalance,
  newBalance,
  creditsAdded,
  onClose,
  title = 'Оплата прошла успешно!',
}: PaymentSuccessModalProps) {
  const [showCounter, setShowCounter] = useState(false);
  const [particles] = useState(() =>
    Array.from({ length: 20 }, (_, i) => i * 0.05)
  );

  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => setShowCounter(true), 600);
      return () => clearTimeout(timer);
    }

    setShowCounter(false);
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(onClose, 3500);
      return () => clearTimeout(timer);
    }
  }, [isOpen, onClose]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />

          <motion.div
            className="relative z-10 mx-5 w-full max-w-sm overflow-hidden rounded-[28px] bg-card px-6 py-9 shadow-[0_12px_60px_rgba(0,0,0,0.4)]"
            initial={{ scale: 0.8, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 10 }}
            transition={{ type: 'spring', duration: 0.5 }}
          >
            <div className="pointer-events-none absolute inset-0 overflow-hidden">
              {particles.map((delay, i) => (
                <Particle key={i} delay={delay} />
              ))}
            </div>

            <motion.div
              className="absolute -top-20 left-1/2 h-[180px] w-[180px] -translate-x-1/2 rounded-full bg-primary/20 blur-3xl"
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2, duration: 0.5 }}
            />

            <div className="relative flex flex-col items-center text-center">
              <motion.div
                className="mb-4 flex h-[72px] w-[72px] items-center justify-center rounded-full bg-primary/[0.12]"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{
                  type: 'spring',
                  stiffness: 200,
                  damping: 15,
                  delay: 0.1,
                }}
              >
                <motion.div
                  initial={{ scale: 0, rotate: -180 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{
                    type: 'spring',
                    stiffness: 200,
                    damping: 15,
                    delay: 0.3,
                  }}
                >
                  <CheckCircle2 className="h-10 w-10 text-primary" />
                </motion.div>
              </motion.div>

              <motion.h2
                className="mb-2 font-benzin text-xl font-semibold"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
              >
                {title}
              </motion.h2>

              <motion.div
                className="mb-5 flex items-center gap-1.5 text-primary"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
              >
                <Sparkles className="h-[18px] w-[18px]" />
                <span className="text-base font-semibold">+{creditsAdded} кредитов</span>
              </motion.div>

              <motion.div
                className="w-full rounded-2xl border border-white/5 p-5"
                style={{ background: 'linear-gradient(to right, hsl(82 64% 56% / 0.1), hsl(187 80% 54% / 0.1))' }}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.6 }}
              >
                <p className="mb-1 text-xs text-muted-foreground">Новый баланс</p>
                <div className="flex items-center justify-center gap-2">
                  <motion.span
                    className="font-benzin text-4xl font-bold text-primary"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.7 }}
                  >
                    {showCounter ? (
                      <AnimatedCounter from={oldBalance} to={newBalance} duration={1200} />
                    ) : (
                      oldBalance
                    )}
                  </motion.span>
                  <motion.div
                    initial={{ opacity: 0, scale: 0, rotate: -180 }}
                    animate={{ opacity: 1, scale: 1, rotate: 0 }}
                    transition={{ delay: 0.8, type: 'spring' }}
                  >
                    <Gem className="h-[26px] w-[26px] text-cyan-400" />
                  </motion.div>
                </div>
              </motion.div>

              <motion.p
                className="mt-4 text-[11px] text-[#5a5a5e]"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.5 }}
              >
                Автоматический переход через 3 сек...
              </motion.p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
